/**
 * The rank card in real 3D: one Pixi `Mesh` per card on screen, whose vertex
 * shader rotates the box (roll, then yaw, then pitch) and projects it through a
 * perspective camera, and whose fragment shader lights the printed stock the way
 * the table lights it — a fixed warm key from the upper left, a cool fill, a rim
 * in the rank's colour, the bevel catching a sharp specular — and then strikes
 * it in the rank's **finish**: paper, linen, silver, rose gold, gold leaf,
 * holographic, spectrum.
 *
 * The print is not replaced by the finish; it is printed ON it. The face texture
 * is split by luminance into ink and substrate, and only the substrate becomes
 * metal, so a gold King still reads as a King — which is the whole trick of a
 * foil card, and the reason the rank stays legible at the top of the ladder.
 *
 * No depth buffer: the card is a convex solid wound outward, so the shader
 * discards back-facing fragments and the front-facing ones are exactly the
 * visible ones. Several cards (the suit fan, the riffle, Blackjack's pair) sort
 * among themselves by draw order, which the CPU sets from their z.
 *
 * WebGL only, like every other shader here — `src/pixi/app.ts` pins WebGL 2.
 */

import {
  Buffer,
  BufferUsage,
  Container,
  Geometry,
  GlProgram,
  Mesh,
  Shader,
  UniformGroup,
  type TextureSource,
} from 'pixi.js';

import { rankPalette } from '@/core/tokens';
import type { CardId } from '@/game/types';
import type { CardUV } from '@/pixi/cardTexture';
import { HALF_HEIGHT, cardGeometry } from './card-geometry';
import type { Finish } from './timeline';

/** The camera's distance in card half-widths. A ~30° lens on the card. */
export const CAM_DIST = 6.5;

const FINISH_INDEX: Readonly<Record<Finish, number>> = {
  paper: 0,
  linen: 1,
  silver: 2,
  rose: 3,
  gold: 4,
  holo: 5,
  spectrum: 6,
};

/** The gilt on the stock's edge, by finish. */
const EDGE_COLOUR: Readonly<Record<Finish, [number, number, number]>> = {
  paper: [0.86, 0.84, 0.8],
  linen: [0.9, 0.88, 0.83],
  silver: [0.88, 0.91, 0.96],
  rose: [0.95, 0.74, 0.68],
  gold: [1.0, 0.82, 0.42],
  holo: [0.9, 0.93, 1.0],
  spectrum: [1.0, 0.96, 0.86],
};

const VERT = /* glsl */ `
in vec3 aPos;
in vec3 aNormal;
in vec2 aUV;
in float aSide;

out vec3 vN;
out vec3 vP;
out vec3 vObj;
out vec3 vObjN;
out vec2 vUV;
out float vSide;

uniform mat3 uProjectionMatrix;
uniform mat3 uWorldTransformMatrix;
uniform mat3 uTransformMatrix;
uniform mat3 uRot;
uniform vec4 uView;   // centre x, centre y (px), px per unit, camera distance
uniform vec4 uPlace;  // offset x, y, z (units), scale

void main() {
  vec3 v = uRot * (aPos * uPlace.w) + uPlace.xyz;
  float persp = uView.w / max(0.001, uView.w - v.z);
  vec2 s = uView.xy + vec2(v.x, -v.y) * persp * uView.z;
  mat3 mvp = uProjectionMatrix * uWorldTransformMatrix * uTransformMatrix;
  gl_Position = vec4((mvp * vec3(s, 1.0)).xy, 0.0, 1.0);
  vN = uRot * aNormal;
  vP = v;
  vObj = aPos;
  vObjN = aNormal;
  vUV = aUV;
  vSide = aSide;
}
`;

const FRAG = /* glsl */ `
precision highp float;
const float HALF_H = ${HALF_HEIGHT.toFixed(4)};

in vec3 vN;
in vec3 vP;
in vec3 vObj;
in vec3 vObjN;
in vec2 vUV;
in float vSide;
out vec4 finalColor;

uniform sampler2D uFace;
uniform sampler2D uNormal;

uniform mat3 uRot;
uniform vec4 uView;
uniform vec4 uFaceRect;   // the print's rect in the plate atlas
uniform vec4 uBackRect;   // the back's rect
uniform vec4 uNormalRect; // the paper tooth's rect
uniform vec3 uBase;       // the rank's colour
uniform vec3 uEdge;       // the gilt on the stock
uniform float uFinish;
uniform float uHeat;      // white-hot 0..1: the swap happens at 1
uniform float uSweep;     // light sweep across the face (off outside -1.5..1.5)
uniform float uTime;      // seconds, from the ceremony clock
uniform float uQuality;   // 1 = lit, 2 = + thin film, sparkle, second sweep lobe
uniform float uFrontSign;
uniform float uAlpha;

float luma(vec3 c) {
  return dot(c, vec3(0.2126, 0.7152, 0.0722));
}

float hash2(vec2 p) {
  return fract(sin(dot(p, vec2(41.3, 289.1))) * 43758.5453);
}

float noise2(vec2 p) {
  vec2 i = floor(p);
  vec2 f = fract(p);
  f = f * f * (3.0 - 2.0 * f);
  return mix(
    mix(hash2(i), hash2(i + vec2(1.0, 0.0)), f.x),
    mix(hash2(i + vec2(0.0, 1.0)), hash2(i + vec2(1.0, 1.0)), f.x),
    f.y);
}

float fbm2(vec2 p) {
  float a = 0.5;
  float s = 0.0;
  for (int i = 0; i < 4; i++) {
    s += a * noise2(p);
    p = p * 2.03 + vec2(1.7, 9.2);
    a *= 0.5;
  }
  return s;
}

// Distance to the nearest of a jittered lattice: gold leaf's crackle, holo's flakes.
vec2 cells2(vec2 p) {
  vec2 i = floor(p);
  vec2 f = fract(p);
  float d1 = 8.0;
  float d2 = 8.0;
  for (int y = -1; y <= 1; y++) {
    for (int x = -1; x <= 1; x++) {
      vec2 o = vec2(float(x), float(y));
      vec2 j = vec2(hash2(i + o), hash2(i + o + 7.1));
      float d = length(o + j - f);
      if (d < d1) { d2 = d1; d1 = d; } else if (d < d2) { d2 = d; }
    }
  }
  return vec2(d1, d2);
}

// The studio the polished finishes reflect: dark floor, lifted sky, a soft box
// up and to the left, a horizon, and a warm wall behind the camera so a face
// turned to us is never a black mirror.
vec3 studio(vec3 r) {
  float sky = smoothstep(-0.35, 0.75, r.y);
  // The floor is a lifted dusk, not black: a black floor under a bright horizon read as a dark seam across
  // the Wild card, where the reflection vector crosses it.
  vec3 c = mix(vec3(0.17, 0.16, 0.22), vec3(0.44, 0.46, 0.54), sky);
  float box = exp(-(pow(r.x + 0.45, 2.0) * 7.0 + pow(r.y - 0.55, 2.0) * 10.0));
  float box2 = exp(-(pow(r.x - 0.7, 2.0) * 14.0 + pow(r.y - 0.2, 2.0) * 22.0));
  float wall = smoothstep(0.1, 1.0, r.z) * (0.55 + 0.45 * sky);
  return c + vec3(2.4, 2.3, 2.15) * box + vec3(0.7, 0.8, 1.0) * box2
    + vec3(0.62, 0.56, 0.5) * wall;
}

// A spectrum, for the holographic and spectrum finishes.
vec3 spectrum(float t) {
  return 0.55 + 0.45 * cos(6.2831 * (vec3(0.0, 0.33, 0.67) + t));
}

void main() {
  if ((gl_FrontFacing ? 1.0 : -1.0) * uFrontSign < 0.0) discard;

  int fin = int(uFinish + 0.5);
  bool isEdge = vSide > 1.5;
  bool isBack = vSide > 0.5 && vSide < 1.5;
  vec4 rect = isBack ? uBackRect : uFaceRect;
  vec3 print = isEdge ? uEdge : texture(uFace, rect.xy + vUV * rect.zw).rgb;

  // The stock's own surface: the baked paper tooth, lit per-pixel.
  //
  // The tooth is a TANGENT-space map and the card's tangent frame is its own
  // axes (mirrored on the back), so the perturbation is built in object space
  // and rotated with the card — not added to an already-rotated normal, which
  // would light the tooth from a fixed direction however the card turns. It is
  // faded out across the bevel, where the geometry's own normal is the truth.
  vec3 nObj = normalize(vObjN);
  if (!isEdge) {
    vec3 t = texture(uNormal, uNormalRect.xy + vUV * uNormalRect.zw).rgb * 2.0 - 1.0;
    float s = isBack ? -1.0 : 1.0;
    vec3 T = vec3(s, 0.0, 0.0);
    vec3 B = vec3(0.0, -1.0, 0.0);
    // Paper takes the full tooth; a struck metal takes a fraction of it. A
    // foil card is a mirror with a grain in it, and at ceremony size the full
    // paper normal made silver read as crumpled kitchen foil.
    float tooth = fin >= 2 ? 0.32 : 0.9;
    float onFace = abs(nObj.z);
    nObj = normalize(nObj + (T * t.x + B * t.y) * onFace * tooth);
  }
  vec3 N = normalize(uRot * nObj);

  vec3 V = normalize(vec3(0.0, 0.0, uView.w) - vP);
  float ndv = max(dot(N, V), 0.0);
  vec3 L = normalize(vec3(-0.55, 0.78, 0.62));
  vec3 L2 = normalize(vec3(0.85, 0.05, 0.45));
  vec3 H = normalize(L + V);
  vec3 R = reflect(-V, N);

  // The bevel: 0 on the flat of the card, rising toward the border's fillet.
  vec3 on = abs(vObjN);
  float bevel = smoothstep(0.02, 0.5, 1.0 - max(on.z, max(on.x, on.y)));

  // Ink versus substrate. Only the substrate is struck in metal; the print
  // stays print, which is what keeps the rank readable at the top of the ladder.
  float ink = 1.0 - smoothstep(0.34, 0.74, luma(print));
  vec2 p = vObj.xy;

  vec3 sub = print;
  float metal = 0.0;
  float gloss = 60.0;
  float envK = 0.06;
  float specK = 0.18;
  vec3 emit = vec3(0.0);

  if (isEdge) {
    sub = uEdge;
    metal = 0.9;
    gloss = 180.0;
    envK = 0.7;
    specK = 1.1;
  } else if (fin == 0) {
    // Paper: printed card stock, barely glossy.
    sub = print * (0.97 + 0.06 * fbm2(p * 9.0));
  } else if (fin == 1) {
    // Linen: the pressed weave of a good deck, cross-hatched.
    float weave = 0.5 + 0.5 * sin(p.x * 260.0) * sin(p.y * 260.0);
    sub = print * (0.94 + 0.12 * weave);
    gloss = 110.0;
    specK = 0.34;
    envK = 0.12;
  } else if (fin == 2 || fin == 3 || fin == 4) {
    // Struck in metal: silver, rose gold, gold leaf. Brushed along the card's
    // long axis, with leaf crackle in the gold.
    vec3 tint = fin == 2 ? vec3(0.82, 0.86, 0.95) : fin == 3 ? vec3(0.96, 0.73, 0.67) : vec3(1.0, 0.70, 0.20);
    float brush = noise2(vec2(p.x * 2.0, p.y * 40.0)) * 0.6 + noise2(vec2(p.x * 44.0, p.y * 1.5)) * 0.4;
    sub = mix(print, tint, 0.74) * (0.96 + 0.08 * brush);
    if (fin == 4) {
      // Leaf: a soft mottle and a faint crackle. (It was a per-cell random brightness, which printed a visible
      // square grid across the whole card.)
      float mott = fbm2(p * 4.0 + 3.1);
      // Luster: a broad anisotropic band that slides across the leaf as the card tilts (it follows the normal), so
      // the gold has a bright side and a deep side instead of one even cream.
      float aniso = 0.5 + 0.5 * sin((p.x * 0.9 + p.y * 0.5 + nObj.x * 2.4 - nObj.y * 1.2) * 3.4 + uTime * 0.3);
      sub *= 0.72 + 0.24 * mott + 0.4 * aniso;
      vec2 c = cells2(p * 7.0);
      float seam = 1.0 - smoothstep(0.0, 0.05, c.y - c.x);
      sub = mix(sub, tint * 1.2, seam * 0.14);
    }
    metal = 1.0;
    gloss = fin == 2 ? 240.0 : 200.0;
    envK = fin == 4 ? 1.7 : 1.25;
    specK = fin == 4 ? 1.55 : 1.2;
  } else if (fin == 5) {
    // Holographic: a thin film over the print, the hue turning with the view,
    // and a scatter of flakes that catch the key light.
    float band = ndv * 3.6 + (p.x * 1.1 - p.y * 0.8) * 1.6 + uTime * 0.08;
    vec3 film = spectrum(band * 0.16);
    // A second film layer sits "deeper": it slides against the first as the card tilts (parallax), so the foil has
    // depth instead of one flat gradient.
    vec2 par = nObj.xy * 0.55 + (V.xy - vec2(0.0)) * 0.25;
    float band2 = ndv * 2.6 + ((p.x + par.x) * 0.8 + (p.y + par.y) * 1.3) * 1.8 - uTime * 0.05;
    film = mix(film, spectrum(band2 * 0.19 + 0.4), 0.42);
    sub = mix(print, film, 0.76) * (0.9 + 0.18 * fbm2(p * 7.0));
    // A persistent edge shimmer: the border carries a slow spectrum of its own, so the holo has depth at rest.
    emit += spectrum(uTime * 0.14 + p.x * 0.7 - p.y * 0.45) * smoothstep(0.05, 0.45, bevel) * 0.6;
    if (uQuality > 1.5) {
      vec2 c = cells2((p + nObj.xy * 0.4) * 26.0);
      float flake = 1.0 - smoothstep(0.0, 0.28, c.x);
      float sp = pow(max(dot(reflect(-L, N), V), 0.0), 240.0);
      emit += spectrum(hash2(floor((p + nObj.xy * 0.4) * 26.0))) * flake * sp * 2.2;
    }
    metal = 0.75;
    gloss = 320.0;
    envK = 1.0;
    specK = 1.25;
    emit += film * 0.1;
  } else {
    // Spectrum foil: the top of the ladder. The whole substrate is a moving
    // rainbow, brightest where the card turns away, and it lifts the ink too.
    float band = ndv * 2.2 + (p.x * 0.9 + p.y * 1.4) * 1.9 + uTime * 0.22;
    vec3 film = spectrum(band * 0.15);
    vec2 par = nObj.xy * 0.6 + V.xy * 0.3;
    float band2 = ndv * 1.8 + ((p.x + par.x) * 1.2 - (p.y + par.y) * 0.7) * 2.1 - uTime * 0.12;
    film = mix(film, spectrum(band2 * 0.17 + 0.5), 0.4);
    float fres = pow(1.0 - ndv, 1.4);
    // Rich, not garish: the foil is mixed at two thirds and lifted toward
    // white, so the court figure's ink still draws the card.
    sub = mix(print, mix(film, vec3(1.0), 0.18), 0.66) * (0.95 + 0.12 * fbm2(p * 5.0));
    sub = mix(sub, film * 1.15, fres * 0.55);
    emit += film * (0.16 + 0.42 * bevel + 0.3 * fres);
    emit += spectrum(uTime * 0.12 - p.x * 0.6 + p.y * 0.5) * smoothstep(0.05, 0.45, bevel) * 0.5;
    metal = 0.85;
    gloss = 420.0;
    envK = 1.15;
    specK = 1.4;
  }

  // A pressed foil edge and an embossed inner frame on the plain stocks (paper, linen, silver, rose), in the rank's
  // own light: the first ranks have no court art to carry them, so the stock itself has to say it is a ceremony.
  if (!isEdge && fin <= 3) {
    float foilEdge = smoothstep(0.08, 0.5, bevel);
    float ringD = abs(max(abs(vObj.x), abs(vObj.y) / HALF_H) - 0.945);
    float ring = (1.0 - smoothstep(0.0, 0.014, ringD)) * (fin <= 1 ? 1.0 : 0.55);
    float foilK = max(foilEdge, ring);
    vec3 foilCol = mix(uBase, vec3(1.0, 0.96, 0.88), 0.4) * 1.15;
    sub = mix(sub, foilCol, foilK * 0.9);
    metal = max(metal, foilK * 0.9);
    gloss = mix(gloss, 320.0, foilK);
    envK = mix(envK, 1.2, foilK);
    specK = mix(specK, 1.2, foilK);
  }

  // The print, over the substrate. Metals darken and gloss the ink rather than
  // erasing it; paper leaves it exactly as baked.
  vec3 alb = mix(sub, print * mix(1.0, 0.35, metal), ink * (isEdge ? 0.0 : 1.0));
  metal *= 1.0 - ink * 0.65;
  // A thin bezel in the rank's colour on every stock, so even paper says how far up you are.
  alb = mix(alb, mix(alb, uBase, 0.6), bevel * (isEdge ? 0.0 : 1.0));

  float key = max(dot(N, L), 0.0);
  float fill = max(dot(N, L2), 0.0);
  vec3 diffuse = alb * (0.3 + 0.88 * key + vec3(0.55, 0.62, 0.8) * 0.3 * fill);
  vec3 specCol = mix(vec3(1.0), alb * 1.25, metal);
  float spec = pow(max(dot(N, H), 0.0), gloss) * specK * (0.4 + gloss / 200.0);
  // The border's fillet: a sharp glint only its sweeping normals satisfy.
  spec += pow(max(dot(N, H), 0.0), 420.0) * 0.8 * bevel;
  vec3 F0 = mix(vec3(0.04), alb, metal);
  vec3 F = F0 + (1.0 - F0) * pow(1.0 - ndv, 5.0);
  vec3 env = studio(R) * F * envK;
  vec3 col = diffuse * (1.0 - metal) + alb * metal * (0.24 + 0.5 * key) + specCol * spec + env;

  // The back is lit too (it was a flat purple card): a pool of the rank's light on the diamond and a rim.
  if (isBack) {
    float rr = length(vec2(vObj.x, vObj.y / HALF_H));
    emit += mix(uBase, vec3(1.0), 0.35) * (0.28 * smoothstep(1.1, 0.0, rr) + 0.18 * pow(1.0 - ndv, 2.0));
  }

  // A rim in the rank's own colour: the silhouette separates from the veil.
  col += mix(uBase, vec3(1.0), 0.4) * pow(1.0 - ndv, 3.5) * 0.28;
  // The first stocks (paper, linen) are flat to the camera, where the grazing-angle rim above is nil: a rim light on the
  // card's own edge (the bevel) in the rank's colour, so Seven's card is separated from the ring behind it.
  if (!isBack && fin <= 1) col += mix(uBase, vec3(1.0), 0.6) * smoothstep(0.04, 0.7, bevel) * 0.55;
  col += emit;

  // The sweep: a diagonal band crossing the face (the level-up's shine, the
  // rank-up's landing).
  if (uSweep > -1.6 && uSweep < 1.6) {
    float band = (vP.x * 0.8 + vP.y * 0.4) - uSweep * 1.9;
    float s = exp(-band * band * 12.0);
    if (uQuality > 1.5) s += 0.35 * exp(-pow(band + 0.3, 2.0) * 55.0);
    col += vec3(1.0, 0.97, 0.9) * s * (0.35 + 0.65 * ndv) * 0.85;
  }

  // White-hot: the face changes inside the light. The card stays LIT inside it (a rim, a falloff across the
  // face, the stock's own colour breathing through), so at the swap it reads as a glowing card and not as a
  // flat cream placeholder.
  vec3 hot = vec3(1.0, 0.96, 0.88) * (0.66 + 0.5 * key + 1.0 * pow(1.0 - ndv, 2.2));
  hot = mix(hot, hot * (0.55 + 0.7 * alb), 0.7);
  col = mix(col, hot, clamp(uHeat, 0.0, 1.0) * 0.66);
  // A rim in the rank's own light at the swap, so the card has an edge inside the glare.
  col += mix(uBase, vec3(1.0), 0.5) * pow(1.0 - ndv, 2.5) * 0.7 * clamp(uHeat, 0.0, 1.0);

  // A soft shoulder instead of a clip.
  col = col / (1.0 + max(vec3(0.0), col - 0.8) * 0.6);
  finalColor = vec4(col, 1.0) * uAlpha;
}
`;

/** Which way `gl_FrontFacing` reads through Pixi's projection. */
const FRONT_SIGN = 1;

let program: GlProgram | null = null;
function getProgram(): GlProgram {
  program ??= new GlProgram({ vertex: VERT, fragment: FRAG, name: 'w21-rank-card' });
  return program;
}

let geometry: Geometry | null = null;
function getGeometry(): Geometry {
  if (geometry) return geometry;
  const g = cardGeometry();
  geometry = new Geometry({
    attributes: {
      aPos: { buffer: new Buffer({ data: g.positions, usage: BufferUsage.VERTEX }), format: 'float32x3' },
      aNormal: { buffer: new Buffer({ data: g.normals, usage: BufferUsage.VERTEX }), format: 'float32x3' },
      aUV: { buffer: new Buffer({ data: g.uvs, usage: BufferUsage.VERTEX }), format: 'float32x2' },
      aSide: { buffer: new Buffer({ data: g.sides, usage: BufferUsage.VERTEX }), format: 'float32' },
    },
    indexBuffer: new Buffer({ data: g.indices, usage: BufferUsage.INDEX | BufferUsage.COPY_DST }),
  });
  return geometry;
}

/** One card, as the scene asks for it. Angles in degrees, offsets in card half-widths. */
export interface CardSpec {
  /** Which plate to print on the front. */
  readonly face: CardId;
  readonly yaw: number;
  readonly pitch: number;
  readonly roll?: number;
  readonly x?: number;
  readonly y?: number;
  readonly z?: number;
  readonly scale?: number;
  readonly finish: Finish;
  /** The rank's index, for the rim colour. */
  readonly rankIndex: number;
  readonly heat?: number;
  readonly sweep?: number;
  readonly alpha?: number;
}

export interface Plates {
  readonly faceSource: TextureSource;
  readonly normalSource: TextureSource;
  readonly faceUV: ReadonlyMap<CardId, CardUV>;
  readonly backUV: CardUV;
  readonly normalUV: CardUV;
}

const DEG = Math.PI / 180;

/** Rotation for roll → yaw → pitch, column-major for GLSL. */
export function rotation(out: Float32Array, yawDeg: number, pitchDeg: number, rollDeg = 0): Float32Array {
  const cy = Math.cos(yawDeg * DEG);
  const sy = Math.sin(yawDeg * DEG);
  const cp = Math.cos(pitchDeg * DEG);
  const sp = Math.sin(pitchDeg * DEG);
  const cr = Math.cos(rollDeg * DEG);
  const sr = Math.sin(rollDeg * DEG);
  // R = Rx(pitch) * Ry(yaw) * Rz(roll), written out.
  const m = [
    cy * cr,
    -cy * sr,
    sy,
    sp * sy * cr + cp * sr,
    -sp * sy * sr + cp * cr,
    -sp * cy,
    -cp * sy * cr + sp * sr,
    cp * sy * sr + sp * cr,
    cp * cy,
  ];
  // Row-major above → column-major for GLSL.
  for (let col = 0; col < 3; col++) {
    for (let row = 0; row < 3; row++) out[col * 3 + row] = m[row * 3 + col]!;
  }
  return out;
}

function rankColour(index: number): [number, number, number] {
  const hex = rankPalette[Math.min(Math.max(0, index), rankPalette.length - 1)] ?? '#ffffff';
  const n = Number.parseInt(hex.slice(1), 16);
  return [((n >> 16) & 255) / 255, ((n >> 8) & 255) / 255, (n & 255) / 255];
}

function writeRect(target: Float32Array, uv: CardUV): void {
  target[0] = uv.x;
  target[1] = uv.y;
  target[2] = uv.w;
  target[3] = uv.h;
}

class CardMesh {
  readonly mesh: Mesh<Geometry, Shader>;
  readonly #uniforms: UniformGroup;
  readonly #rot = new Float32Array(9);

  constructor(plates: Plates, quality: 1 | 2) {
    this.#uniforms = new UniformGroup({
      uRot: { value: this.#rot, type: 'mat3x3<f32>' },
      uView: { value: new Float32Array([0, 0, 100, CAM_DIST]), type: 'vec4<f32>' },
      uPlace: { value: new Float32Array([0, 0, 0, 1]), type: 'vec4<f32>' },
      uFaceRect: { value: new Float32Array([0, 0, 1, 1]), type: 'vec4<f32>' },
      uBackRect: { value: new Float32Array([0, 0, 1, 1]), type: 'vec4<f32>' },
      uNormalRect: { value: new Float32Array([0, 0, 1, 1]), type: 'vec4<f32>' },
      uBase: { value: new Float32Array([1, 1, 1]), type: 'vec3<f32>' },
      uEdge: { value: new Float32Array([0.9, 0.88, 0.83]), type: 'vec3<f32>' },
      uFinish: { value: 0, type: 'f32' },
      uHeat: { value: 0, type: 'f32' },
      uSweep: { value: -9, type: 'f32' },
      uTime: { value: 0, type: 'f32' },
      uQuality: { value: quality, type: 'f32' },
      uFrontSign: { value: FRONT_SIGN, type: 'f32' },
      uAlpha: { value: 1, type: 'f32' },
    });
    this.mesh = new Mesh<Geometry, Shader>({
      geometry: getGeometry(),
      shader: new Shader({
        glProgram: getProgram(),
        resources: {
          uFace: plates.faceSource,
          uNormal: plates.normalSource,
          cardUniforms: this.#uniforms,
        },
      }),
    });
    writeRect(this.#uniforms.uniforms.uBackRect as Float32Array, plates.backUV);
    writeRect(this.#uniforms.uniforms.uNormalRect as Float32Array, plates.normalUV);
  }

  /**
   * Which way round the winding reads.
   *
   * Rendering into a RenderTexture flips Y, which flips `gl_FrontFacing` with
   * it — and the shader culls by that, so a card baked to a texture without
   * this shows its far side: the back of the card, mirrored. The low tier's
   * bake (`still-bake.ts`) is the only caller.
   */
  facing(sign: 1 | -1): void {
    this.#uniforms.uniforms.uFrontSign = sign;
    this.#uniforms.update();
  }

  /** Point the camera: centre in px, how many px one card half-width is. */
  view(cx: number, cy: number, pxPerUnit: number): void {
    const v = this.#uniforms.uniforms.uView as Float32Array;
    v[0] = cx;
    v[1] = cy;
    v[2] = pxPerUnit;
    v[3] = CAM_DIST;
    this.#uniforms.update();
  }

  set(spec: CardSpec, plates: Plates, timeSeconds: number): void {
    const u = this.#uniforms.uniforms;
    rotation(this.#rot, spec.yaw, spec.pitch, spec.roll ?? 0);
    const place = u.uPlace as Float32Array;
    place[0] = spec.x ?? 0;
    place[1] = spec.y ?? 0;
    place[2] = spec.z ?? 0;
    place[3] = spec.scale ?? 1;
    const face = plates.faceUV.get(spec.face);
    if (face) writeRect(u.uFaceRect as Float32Array, face);
    const base = u.uBase as Float32Array;
    const [r, g, bl] = rankColour(spec.rankIndex);
    base[0] = r;
    base[1] = g;
    base[2] = bl;
    const edge = u.uEdge as Float32Array;
    const ec = EDGE_COLOUR[spec.finish];
    edge[0] = ec[0];
    edge[1] = ec[1];
    edge[2] = ec[2];
    u.uFinish = FINISH_INDEX[spec.finish];
    u.uHeat = spec.heat ?? 0;
    u.uSweep = spec.sweep ?? -9;
    u.uTime = timeSeconds;
    u.uAlpha = spec.alpha ?? 1;
    this.#uniforms.update();
    this.mesh.visible = (spec.alpha ?? 1) > 0.002;
    // Nearer the camera draws later: several cards sort by draw order.
    this.mesh.zIndex = spec.z ?? 0;
  }

  destroy(): void {
    this.mesh.destroy();
  }
}

/**
 * A pool of card meshes in one container. The scene asks for as many cards as a
 * frame needs (`draw`) and the rest are hidden — no allocation per frame, and
 * the pool is the only thing that knows how many cards a ceremony can show.
 */
export class CardView extends Container {
  readonly #plates: Plates;
  readonly #quality: 1 | 2;
  readonly #pool: CardMesh[] = [];
  #cx = 0;
  #cy = 0;
  #px = 100;
  #facing: 1 | -1 = 1;

  constructor(plates: Plates, quality: 1 | 2 = 2) {
    super();
    this.#plates = plates;
    this.#quality = quality;
    this.sortableChildren = true;
  }

  /** Invert the winding test — for rendering into a texture. See `CardMesh.facing`. */
  facing(sign: 1 | -1): void {
    this.#facing = sign;
    for (const card of this.#pool) card.facing(sign);
  }

  /** The camera, in the container's own pixels. */
  camera(cx: number, cy: number, pxPerUnit: number): void {
    this.#cx = cx;
    this.#cy = cy;
    this.#px = pxPerUnit;
    for (const card of this.#pool) card.view(cx, cy, pxPerUnit);
  }

  /** Draw exactly these cards, in this order, and hide the rest of the pool. */
  draw(specs: readonly CardSpec[], timeSeconds: number): void {
    while (this.#pool.length < specs.length) {
      const card = new CardMesh(this.#plates, this.#quality);
      card.view(this.#cx, this.#cy, this.#px);
      card.facing(this.#facing);
      this.#pool.push(card);
      this.addChild(card.mesh);
    }
    for (let i = 0; i < this.#pool.length; i++) {
      const card = this.#pool[i]!;
      const spec = specs[i];
      if (spec) card.set(spec, this.#plates, timeSeconds);
      else card.mesh.visible = false;
    }
  }

  override destroy(): void {
    for (const card of this.#pool) card.destroy();
    this.#pool.length = 0;
    super.destroy();
  }
}
